// Branded, promise-based confirmation dialog to replace native window.confirm().
//
// Why: window.confirm() renders the browser's own "app.9fs.ai says" chrome — no
// branding, and it swallows Escape (Esc does not reliably dismiss a native confirm).
// This gives a themed modal that honours the design tokens, closes on Escape and
// backdrop click, and confirms on Enter.
//
// Usage:
//   const confirm = useConfirm()
//   if (!(await confirm('Delete this experiment?'))) return
//   // or, for destructive actions:
//   if (!(await confirm({ message: 'Delete this experiment?', danger: true,
//                         confirmLabel: 'Delete' }))) return
//
// Mount <ConfirmProvider> once near the app root (see main.tsx).

import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react'
import './ConfirmDialog.css'

interface ConfirmOpts {
  message: string
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
  /** alert mode: single OK button, no Cancel (still dismissable via Esc / backdrop). */
  alert?: boolean
}

type ConfirmFn = (opts: string | ConfirmOpts) => Promise<boolean>

const ConfirmCtx = createContext<ConfirmFn>(async () => false)

/** Returns confirm(msgOrOpts) => Promise<boolean>. Resolves true on OK/Enter,
 *  false on Cancel/Escape/backdrop. */
export function useConfirm(): ConfirmFn {
  return useContext(ConfirmCtx)
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOpts | null>(null)
  const resolver = useRef<((v: boolean) => void) | null>(null)
  const okRef = useRef<HTMLButtonElement>(null)

  const confirm = useCallback<ConfirmFn>((arg) => {
    const o = typeof arg === 'string' ? { message: arg } : arg
    setOpts(o)
    return new Promise<boolean>((resolve) => { resolver.current = resolve })
  }, [])

  const settle = useCallback((v: boolean) => {
    resolver.current?.(v)
    resolver.current = null
    setOpts(null)
  }, [])

  useEffect(() => {
    if (!opts) return
    okRef.current?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); settle(false) }
      else if (e.key === 'Enter') { e.preventDefault(); settle(true) }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [opts, settle])

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {opts && (
        <div className="cfd-backdrop" onMouseDown={() => settle(false)}>
          <div
            className="cfd-modal"
            role="alertdialog"
            aria-modal="true"
            aria-label={opts.title || 'Confirm'}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="cfd-title">{opts.title || 'Please confirm'}</div>
            <div className="cfd-message">{opts.message}</div>
            <div className="cfd-actions">
              {!opts.alert && (
                <button className="cfd-btn cfd-cancel" onClick={() => settle(false)}>
                  {opts.cancelLabel || 'Cancel'}
                </button>
              )}
              <button
                ref={okRef}
                className={`cfd-btn ${opts.danger ? 'cfd-danger' : 'cfd-ok'}`}
                onClick={() => settle(true)}
              >
                {opts.confirmLabel || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  )
}
